import React, { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Property } from '@/types/property';
import { ExternalLink, ArrowUpDown, TrendingUp, TrendingDown, Minus, BedDouble, ShowerHead, Car } from 'lucide-react';

interface PropertyTableProps {
  loading: boolean;
  dataLoaded: boolean;
  databaseError: string | null;
  properties: Property[];
  dashboardType?: 'venda' | 'aluguel' | 'sairam-ultima-semana' | 'entraram-ultima-semana';
  isMobile?: boolean;
}

const PropertyTable: React.FC<PropertyTableProps> = ({ 
  loading,
  dataLoaded,
  databaseError,
  properties, 
  isMobile = false,
  dashboardType = 'venda'
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(isMobile ? 10 : 25);
  const [sortColumn, setSortColumn] = useState<keyof Property | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  const priceLabel = dashboardType === 'venda' ? 'Valor' : dashboardType === 'aluguel' ? 'Aluguel' : 'Valor';
  const showLink = dashboardType !== 'sairam-ultima-semana';

  useEffect(() => {
    setItemsPerPage(isMobile ? 10 : 25);
  }, [isMobile]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortColumn, sortDirection, properties]);

  // Log para debug (opcional, pode remover depois)
  useEffect(() => {
    const idCounts = properties.reduce((acc, prop) => {
      acc[prop.id || 'undefined'] = (acc[prop.id || 'undefined'] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    console.log('ID Counts:', idCounts);
  }, [properties]);

  const getNumericValue = (value: any): number => {
    if (value == null) return -1;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[^\d,.]/g, '').replace(',', '.');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? -1 : parsed;
    }
    return -1;
  };

  const sortedProperties = React.useMemo(() => {
    if (!sortColumn) return [...properties];

    return [...properties].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (
        sortColumn === 'numero_quartos' || 
        sortColumn === 'numero_banheiros' || 
        sortColumn === 'numero_vagas' ||
        sortColumn === 'area_util' ||
        sortColumn === 'area_total' ||
        sortColumn === 'valor_recente'
      ) {
        const aNum = getNumericValue(aValue);
        const bNum = getNumericValue(bValue);
        
        if (aNum === -1 && bNum === -1) return 0;
        if (aNum === -1) return 1;
        if (bNum === -1) return -1;
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue == null) return sortDirection === 'asc' ? -1 : 1;
      
      return sortDirection === 'asc' 
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [properties, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedProperties.length / itemsPerPage);

  const currentItems = sortedProperties.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatCurrency = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return '-';
    
    try { 
      if (typeof value === 'number') {
        return value.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          maximumFractionDigits: 0
        });
      }
      
      if (typeof value === 'string' && value.includes('R$')) return value;
      
      const numericValue = typeof value === 'string' 
        ? value.replace(/[^\d,\.]/g, '').replace(',', '.')
        : String(value);
        
      const price = parseFloat(numericValue);
      if (isNaN(price)) return value;
      
      return price.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0
      });
    } catch (error) {
      console.error('Error formatting currency:', error);
      return value || '-';
    }
  };

  const renderVariationIcon = (variation: string | null) => {
    if (variation === 'Subiu') {
      return <TrendingUp className="h-4 w-4 text-green-500" />;
    }
    if (variation === 'Desceu') {
      return <TrendingDown className="h-4 w-4 text-red-500" />;
    }
    return <Minus className="h-4 w-4 text-gray-500" />;
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = isMobile ? 3 : 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const handleSort = (column: keyof Property | null) => {
    if (column === sortColumn) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const formatArea = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return '-';
    
    try {
      if (typeof value === 'number') {
        return value.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
      }
      
      if (typeof value === 'string' && !isNaN(Number(value.replace(',', '.')))) {
        return Number(value.replace(',', '.')).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
      }
      
      return value || '-';
    } catch (error) {
      console.error('Error formatting area:', error);
      return value || '-';
    }
  };

  if (properties.length === 0) {
    return (
      <div className="text-center py-4 md:py-6">
        <p className="text-sm md:text-base text-muted-foreground">Nenhum imóvel encontrado com os filtros aplicados.</p>
        <p className="text-xs md:text-sm text-muted-foreground mt-2">Atualize a página ou consulte o suporte.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4">
      <div className={`flex ${isMobile ? 'flex-col' : 'justify-between'} items-start md:items-center mb-2 md:mb-4 gap-2`}>
        <div className="text-xs md:text-sm text-muted-foreground">
          Total de {properties.length.toLocaleString('pt-BR')} imóveis encontrados
        </div>
        <div className={`flex ${isMobile ? 'flex-col w-full' : 'items-center'} gap-2 md:gap-4`}>
          <div className="flex items-center gap-1 md:gap-2">
            <label htmlFor="sortColumn" className="text-xs md:text-sm text-muted-foreground">
              Ordenar:
            </label>
            <select
              id="sortColumn"
              value={sortColumn || ''}
              onChange={(e) => handleSort(e.target.value as keyof Property || null)}
              className="text-xs md:text-sm rounded border border-gray-300 px-1 py-0.5 md:px-2 md:py-1"
            >
              <option value="">Nenhum</option>
              <option value="tipo">Tipo</option>
              <option value="bairro">Bairro</option>
              <option value="numero_quartos">Quartos</option>
              <option value="numero_banheiros">Banheiros</option>
              <option value="numero_vagas">Vagas</option>
              <option value="area_util">Útil m²</option>
              <option value="area_total">Total m²</option>
              <option value="valor_recente">{priceLabel}</option>
              <option value="variacao">Variação</option>
            </select>
            <button
              onClick={() => handleSort(sortColumn)}
              className="text-blue-600 hover:text-blue-800"
              title={sortDirection === 'asc' ? 'Ordenar descendente' : 'Ordenar ascendente'}
            >
              <ArrowUpDown className="h-3 w-3 md:h-4 md:w-4" />
            </button>
          </div>
          {!isMobile && (
            <div className="flex items-center gap-2">
              <label htmlFor="itemsPerPage" className="text-xs md:text-sm text-muted-foreground">
                Por página:
              </label>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="text-xs md:text-sm rounded border border-gray-300 px-1 py-0.5 md:px-2 md:py-1"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                {!isMobile && <option value={200}>200</option>}
              </select>
            </div>
          )}
        </div>
      </div>
      
      <div className="relative overflow-x-auto">
        <Table className="w-full">
          <TableHeader>
            <TableRow>
              <TableHead className={`${isMobile ? 'text-xs px-2' : ''}`}>Tipo</TableHead>
              <TableHead className={`${isMobile ? 'text-xs px-2' : ''}`}>Bairro</TableHead>
              {!isMobile && (
                <TableHead>
                  <BedDouble size={20} />
                </TableHead>
              )}
              {!isMobile && (
                <TableHead>
                  <ShowerHead size={20} />
                </TableHead>
              )}
              {!isMobile && (
                <TableHead>
                  <Car size={20} />
                </TableHead>
              )}
              <TableHead className={`${isMobile ? 'text-xs px-2' : ''}`}>Útil m²</TableHead>
              <TableHead className={`${isMobile ? 'text-xs px-2' : ''}`}>Total m²</TableHead>
              <TableHead className={`${isMobile ? 'text-xs px-2' : ''}`}>{priceLabel}</TableHead>
              {!isMobile && <TableHead>Variação</TableHead>}
              {showLink && (
                <TableHead className={`text-right ${isMobile ? 'text-xs px-2' : ''}`}>Link</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentItems.map((property, index) => (
              <TableRow key={property.id ? `property-${property.id}-${index}` : `index-${index}`}>
                <TableCell className={`font-medium ${isMobile ? 'py-1 text-xs px-2' : ''}`}>{property.tipo || '-'}</TableCell>
                <TableCell className={`${isMobile ? 'py-1 text-xs truncate max-w-[80px] px-2' : ''}`}>{property.bairro || '-'}</TableCell>
                {!isMobile && (
                  <TableCell>
                    {typeof property.numero_quartos === 'number' 
                      ? property.numero_quartos 
                      : parseInt(String(property.numero_quartos), 10) || 0}
                  </TableCell>
                )}
                {!isMobile && (
                  <TableCell>
                    {typeof property.numero_banheiros === 'number' 
                      ? property.numero_banheiros 
                      : parseInt(String(property.numero_banheiros), 10) || 0}
                  </TableCell>
                )}
                {!isMobile && (
                  <TableCell>
                    {typeof property.numero_vagas === 'number' 
                      ? property.numero_vagas 
                      : parseInt(String(property.numero_vagas), 10) || 0}
                  </TableCell>
                )}
                <TableCell className={`${isMobile ? 'py-1 text-xs px-2' : ''}`}>{formatArea(property.area_util)}</TableCell>
                <TableCell className={`${isMobile ? 'py-1 text-xs px-2' : ''}`}>{formatArea(property.area_total)}</TableCell>
                <TableCell className={`${isMobile ? 'py-1 text-xs px-2' : ''}`}>{formatCurrency(property.valor_recente)}</TableCell>
                {!isMobile && (
                  <TableCell className="text-center">
                    {renderVariationIcon(property.variacao)}
                  </TableCell>
                )}
                {showLink && (
                  <TableCell className="text-right">
                    {property.url && (
                      <a 
                        href={property.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                      </a>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent className={isMobile ? "space-x-1" : ""}>
            <PaginationItem>
              <PaginationPrevious 
                onClick={handlePrevPage} 
                className={`${currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'} ${isMobile ? 'h-8 w-8 p-0' : ''}`}
              />
            </PaginationItem>
            
            {getPageNumbers().map(page => (
              <PaginationItem key={page}>
                <PaginationLink 
                  onClick={() => goToPage(page)}
                  isActive={page === currentPage}
                  className={isMobile ? "h-8 w-8 p-0 text-xs" : ""}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            
            <PaginationItem>
              <PaginationNext 
                onClick={handleNextPage} 
                className={`${currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'} ${isMobile ? 'h-8 w-8 p-0' : ''}`}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
      
      <div className="text-xs md:text-sm text-muted-foreground text-center">
        Mostrando {Math.min(itemsPerPage, currentItems.length)} de {properties.length.toLocaleString('pt-BR')} imóveis (página {currentPage} de {totalPages || 1})
      </div>
    </div>
  );
};

export default PropertyTable;